import DOMPurify from "dompurify";

// Whitelist for rich text editors - safe formatting only
const RICH_TEXT_ALLOWED_TAGS = [
  "p", "br", "strong", "b", "em", "i", "u",
  "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6",
  "blockquote", "pre", "code"
];

const RICH_TEXT_ALLOWED_ATTR = ["class", "style"];

// Safe style properties for text alignment
const ALLOWED_STYLES = ["text-align"];

/**
 * Sanitizes HTML content to prevent XSS attacks
 */
export function sanitizeHtml(dirty: string, options?: {
  allowedTags?: string[];
  allowedAttributes?: string[];
}): string {
  if (!dirty) return "";
  
  const config = {
    ALLOWED_TAGS: options?.allowedTags || [
      "b", "i", "em", "strong", "a", "p", "br", 
      "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6"
    ],
    ALLOWED_ATTR: options?.allowedAttributes || ["href", "title", "target"],
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
  };

  return String(DOMPurify.sanitize(dirty, config));
}

/**
 * Sanitizes rich text HTML from TipTap editor - stricter config
 */
export function sanitizeRichText(dirty: string): string {
  if (!dirty) return "";
  
  // Remove script tags, event handlers, and dangerous protocols first
  let cleaned = dirty
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
    .replace(/<iframe[^>]*>[\s\S]*?<\/iframe>/gi, "")
    .replace(/<object[^>]*>[\s\S]*?<\/object>/gi, "")
    .replace(/<embed[^>]*>/gi, "")
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, "")
    .replace(/javascript:/gi, "")
    .replace(/vbscript:/gi, "")
    .replace(/data:/gi, "");
  
  const config = {
    ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS,
    ALLOWED_ATTR: RICH_TEXT_ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input", "button"],
    FORBID_ATTR: ["onclick", "onerror", "onload", "onmouseover", "onfocus", "onblur"],
  };

  return String(DOMPurify.sanitize(cleaned, config));
}

/**
 * Sanitizes name fields - removes HTML, special chars
 */
export function sanitizeName(name: string): string {
  if (!name) return "";
  
  // Remove HTML tags completely
  const noHtml = name.replace(/<[^>]*>/g, "");
  
  // Remove potentially dangerous characters but keep letters, numbers, spaces, and common punctuation
  const cleaned = noHtml.replace(/[<>{}[\]\\^`|~]/g, "");
  
  return cleaned.trim().slice(0, 100);
}

/**
 * Validates and sanitizes video URLs - only allows YouTube and Vimeo
 */
export function sanitizeVideoUrl(url: string): string {
  if (!url) return "";
  
  const trimmed = url.trim();
  
  // Block dangerous protocols
  if (
    trimmed.toLowerCase().startsWith("javascript:") ||
    trimmed.toLowerCase().startsWith("data:") ||
    trimmed.toLowerCase().startsWith("vbscript:")
  ) {
    return "";
  }
  
  // Only allow YouTube and Vimeo
  const youtubePattern = /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//i;
  const vimeoPattern = /^https?:\/\/(www\.)?vimeo\.com\//i;
  
  if (youtubePattern.test(trimmed) || vimeoPattern.test(trimmed)) {
    return trimmed;
  }
  
  return "";
}

/**
 * Sanitizes plain text by removing HTML tags (not escaping for display)
 */
export function sanitizeText(text: string): string {
  if (!text) return "";
  
  // Remove HTML tags
  const noHtml = text.replace(/<[^>]*>/g, "");
  
  // Remove dangerous patterns
  const cleaned = noHtml
    .replace(/javascript:/gi, "")
    .replace(/vbscript:/gi, "")
    .replace(/on\w+=/gi, "");
  
  return cleaned.trim();
}

/**
 * Escapes HTML entities for safe display
 */
export function escapeHtml(text: string): string {
  if (!text) return "";
  
  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#x27;",
  };
  
  return text.replace(/[&<>"']/g, (char) => map[char]);
}

/**
 * Sanitizes user input for safe display
 */
export function sanitizeInput(input: string, type: "html" | "text" = "text"): string {
  if (!input) return "";
  
  // Trim whitespace
  const trimmed = input.trim();
  
  if (type === "html") {
    return sanitizeHtml(trimmed);
  }
  
  return sanitizeText(trimmed);
}

/**
 * Sanitizes URL to prevent javascript: and data: protocols
 */
export function sanitizeUrl(url: string): string {
  if (!url) return "";
  
  const trimmed = url.trim().toLowerCase();
  
  // Block dangerous protocols
  if (
    trimmed.startsWith("javascript:") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("vbscript:")
  ) {
    return "";
  }
  
  return url.trim();
}

/**
 * Sanitizes phone number (keeps only digits and common separators)
 */
export function sanitizePhone(phone: string): string {
  if (!phone) return "";
  return phone.replace(/[^\d\s\+\-\(\)]/g, "");
}

/**
 * Sanitizes email address
 */
export function sanitizeEmail(email: string): string {
  if (!email) return "";
  return email.trim().toLowerCase();
}

/**
 * Removes SQL injection patterns from input
 */
export function removeSqlInjection(input: string): string {
  if (!input) return "";
  
  const sqlPatterns = [
    /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE)\b)/gi,
    /(--|;|\/\*|\*\/|xp_|sp_)/gi,
    /('|(\\'))/gi,
  ];
  
  let cleaned = input;
  sqlPatterns.forEach((pattern) => {
    cleaned = cleaned.replace(pattern, "");
  });
  
  return cleaned.trim();
}

/**
 * Comprehensive sanitization for form data
 */
export function sanitizeFormData(data: Record<string, any>): Record<string, any> {
  const sanitized: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === "string") {
      // Sanitize based on field name
      if (key.includes("email")) {
        sanitized[key] = sanitizeEmail(value);
      } else if (key.includes("phone") || key.includes("whatsapp")) {
        sanitized[key] = sanitizePhone(value);
      } else if (key.includes("url") || key.includes("website")) {
        sanitized[key] = sanitizeUrl(value);
      } else if (key.includes("html") || key.includes("description")) {
        sanitized[key] = sanitizeHtml(value);
      } else {
        sanitized[key] = sanitizeInput(value, "text");
      }
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}
